import { CircleInfoOutlinedIcon } from '@village/icons';
import { CenteredSpinner } from 'components/centered-spinner';
import { IsExistingPatientField } from 'components/fields/is-existing-patient';
import { SchedulingNotAvailable } from 'components/scheduling-not-available';
import { differenceInCalendarDays } from 'date-fns';
import {
    useAppointmentsGroups,
    useBooking,
    useDepartmentById,
    useMarkets,
    useProviders,
    useReasons,
    useAppOptions,
    useCaptureCountlyEvent,
    useSchedulingUnavailableReasons,
    useSteadyMDDepartmentId,
    useLimitedVisitTypeReasonsFlag,
} from 'hooks';
import { usePrnNextAvailableAppointment } from 'hooks/use-prn-next-available-appointment';
import { concat } from 'ramda';
import { FC, useEffect, useMemo, useCallback, useRef } from 'react';
import { Appointment, Booking } from 'types';
import { filterReasonsByProviders } from 'utils/reason';
import { DatesNavigation, AppointmentsCard } from 'widget/components';
import { ReasonField } from 'widget/components/fields';
import { SwitchToVideoBannerBottom } from 'widget/components/switch-to-video-banner-bottom';
import { getDefaultReason } from 'widget/utils/reason';

import * as Styled from '../styles';

interface Props {
    onSelectAppointment: (appointment: Appointment, booking: Booking) => void;
}

const LocationWidget: FC<Props> = ({ onSelectAppointment }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { setBookingField, booking } = useBooking();
    const widgetOptions = useAppOptions('locationWidget');
    const steadyMDDeptId = useSteadyMDDepartmentId();
    const { addCountlyEvent } = useCaptureCountlyEvent();
    const { data: markets, isFetching: isMarketsFetching } = useMarkets();
    const { data: departments, isFetching: isDepartmentsFetching } = useDepartmentById(booking, widgetOptions.departmentId);
    const { data: steadyMdDepartments, isFetching: isSteadyMDDepartmentsFetching } = useDepartmentById(booking, steadyMDDeptId);
    const { isFetching: isProvidersFetching, data: providers } = useProviders(booking);
    const { data: allReasons, isFetching: isReasonsFetching } = useReasons(booking);
    const { schedulingUnavailableReasons, addSchedulingUnavailableReason, removeSchedulingUnavailableReason } =
        useSchedulingUnavailableReasons();
    const { isAppointmentsFetching, appointmentsGroupings } = useAppointmentsGroups(providers, {
        featuredProviderId: widgetOptions.providerId,
    });

    const { isNextAvailableFetching, availabilityWithoutUnavailablePrnProviders } =
        usePrnNextAvailableAppointment(appointmentsGroupings);

    useEffect(() => {
        if (widgetOptions.enabledForScheduling === false) {
            addSchedulingUnavailableReason('DISABLED');
        }
    }, [widgetOptions.enabledForScheduling]);

    const reasons = useMemo(() => {
        if (!booking.department || providers.length === 0 || !allReasons) {
            return [];
        }

        const filteredReasons = filterReasonsByProviders(
            allReasons,
            providers.filter(({ isprn }) => !isprn),
            booking.departmentAndRelatedDepartments
        );

        if (filteredReasons.length === 0 && booking.isExistingPatient === false) {
            addSchedulingUnavailableReason('NO_REASONS_NEW_PATIENTS');
        } else {
            removeSchedulingUnavailableReason('NO_REASONS_NEW_PATIENTS');
        }

        return filteredReasons;
    }, [booking.department, booking.departmentAndRelatedDepartments, booking.isExistingPatient, allReasons, providers]);

    const { videoReasonsOnly, inOfficeReasonsOnly, oneVisitTypeOnly } = useLimitedVisitTypeReasonsFlag(reasons);

    useEffect(() => {
        if (markets && !booking.market) {
            const market = markets.find(({ market_key }) => market_key === widgetOptions.marketKey);
            setBookingField({ market });
            if (!market) {
                addSchedulingUnavailableReason('NO_MARKET');
            } else {
                removeSchedulingUnavailableReason('NO_MARKET');
            }
        }
    }, [setBookingField, markets, widgetOptions.marketKey, booking.market]);

    useEffect(() => {
        if (departments && !booking.department) {
            const department = departments.find(({ departmentid }) => departmentid === widgetOptions.departmentId);
            setBookingField({ department });
            if (!department) {
                addSchedulingUnavailableReason('NO_DEPARTMENT');
            } else {
                removeSchedulingUnavailableReason('NO_DEPARTMENT');
            }
        }

        //TODO: If no steady MD department, only check departments data is ready, otherwise wait for steady MD department data
        // We should move this logic into the merged useDepartmetns hook that should be shared by the main and location widget
        // Ticket: https://villagemd.atlassian.net/browse/GROW-3774
        if (departments && (!steadyMDDeptId || steadyMdDepartments)) {
            const departmentAndRelatedDepartments = concat(departments ?? [], steadyMdDepartments ?? []);
            setBookingField({ departmentAndRelatedDepartments });
        }
    }, [setBookingField, departments, steadyMdDepartments, widgetOptions, booking.department]);

    useEffect(() => {
        if (reasons.length > 0 && !booking.reason) {
            const defaultReason = getDefaultReason(reasons, booking.isExistingPatient);
            const initialReason = reasons.find(({ id }) => id === widgetOptions.reasonId);
            setBookingField({ reason: initialReason ?? defaultReason });
        }
    }, [setBookingField, reasons, booking.isExistingPatient, booking.reason, widgetOptions.reasonId]);

    const handleSelectAppointment = (appointment: Appointment) => {
        const provider = providers.find(({ providerid }) => providerid === appointment.providerid);

        // This property will be used to help identify the difference between the first day in
        // the widget scheduler and the day of the appointment that was selected.
        const scheduledDaysOut = differenceInCalendarDays(new Date(appointment.date), booking.date);

        addCountlyEvent({
            key: 'selectSlot',
            segmentation: {
                appointmentId: appointment.appointmentid,
                appointmentTypeId: appointment.appointmenttypeid,
                appointmentSlotOrigin: 'widget-location',
                providerId: appointment.providerid,
                providerName: provider?.displayname,
                scheduledDaysOut,
                time: appointment.starttime,
                isPrn: provider?.isprn,
            },
        });
        onSelectAppointment(appointment, booking);
    };

    const visitTypeVideo = 'VIDEO';
    const switchToVideoVisit = useCallback(() => {
        const newReason = reasons.find((reason) => reason.name === booking.reason?.name && reason.visitType === visitTypeVideo);

        setBookingField({ visitType: visitTypeVideo });

        if (newReason) {
            setBookingField({ reason: newReason });
        }

        containerRef.current?.scrollIntoView({ block: 'start' });

        addCountlyEvent({ key: 'updateVisitType' });
    }, [reasons, booking.reason?.name, setBookingField, addCountlyEvent]);

    useEffect(() => {
        if (videoReasonsOnly) {
            switchToVideoVisit();
        }
    }, [videoReasonsOnly, videoReasonsOnly]);

    const shouldShowSwitchToVideoPrompt = Boolean(
        steadyMDDeptId &&
            reasons.length > 0 &&
            booking.visitType !== visitTypeVideo &&
            !inOfficeReasonsOnly && //not in person reasons only
            !oneVisitTypeOnly
    );

    const isFetching =
        isDepartmentsFetching ||
        isSteadyMDDepartmentsFetching ||
        isAppointmentsFetching ||
        isReasonsFetching ||
        isProvidersFetching ||
        isMarketsFetching ||
        isNextAvailableFetching;

    return (
        <Styled.Container>
            <Styled.Title type="sub2">Book Appointment</Styled.Title>
            <Styled.EmergencyNotice type="body2">
                In case of emergency or life threatening illness, call 911 or go to your local ER
            </Styled.EmergencyNotice>
            <Styled.FieldsWrapper ref={containerRef}>
                <IsExistingPatientField />
                <ReasonField reasons={reasons} isReasonsFetching={isReasonsFetching} />
            </Styled.FieldsWrapper>

            {shouldShowSwitchToVideoPrompt && (
                <Styled.Banner status="detail" icon={CircleInfoOutlinedIcon} iconSize={1.5}>
                    Toggle on video visits to view additional appointments
                </Styled.Banner>
            )}
            <DatesNavigation isAppointmentsFetching={isAppointmentsFetching} />

            {schedulingUnavailableReasons.length ? (
                <SchedulingNotAvailable reasons={schedulingUnavailableReasons} location={widgetOptions} />
            ) : (
                <Styled.RelativeContainer>
                    {isFetching ? (
                        <Styled.SpinnerContainer>
                            <Styled.SpinnerWrapper>
                                <CenteredSpinner />
                            </Styled.SpinnerWrapper>
                        </Styled.SpinnerContainer>
                    ) : null}
                    <Styled.AppointmentsContainer $loading={isFetching}>
                        {availabilityWithoutUnavailablePrnProviders.map((group) => (
                            <AppointmentsCard
                                key={`${group.provider.providerid}-${group.department.departmentid}`}
                                appointmentsGroup={group}
                                isAppointmentsFetching={isAppointmentsFetching}
                                onSelectAppointment={handleSelectAppointment}
                            />
                        ))}
                    </Styled.AppointmentsContainer>
                </Styled.RelativeContainer>
            )}
            {shouldShowSwitchToVideoPrompt && <SwitchToVideoBannerBottom handleClick={switchToVideoVisit} />}
        </Styled.Container>
    );
};

export { LocationWidget };
